import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import api from "@/api";

const MAX_POLLS = 8;

export function PaymentSuccess() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [state, setState] = useState("polling"); // polling | paid | failed
  const polls = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("failed"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setState("paid"); return; }
      } catch (e) { /* keep polling */ }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) { setState("failed"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center bg-white rounded-2xl border border-[#EBDDD3] p-10" data-testid="payment-success">
        {state === "polling" && (<>
          <Loader2 className="w-12 h-12 text-[#C65338] mx-auto mb-5 animate-spin" />
          <h1 className="font-display text-2xl font-black text-[#2D231F]">{t("pay.confirming")}</h1>
        </>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-14 h-14 text-[#5C6646] mx-auto mb-5" />
          <h1 className="font-display text-3xl font-black text-[#2D231F] mb-2">{t("pay.success")}</h1>
          <p className="text-[#8C7A70] mb-6">{t("pay.successMsg")}</p>
          <Button data-testid="pay-to-dashboard" onClick={() => nav("/dashboard")} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white">{t("pay.toDashboard")}</Button>
        </>)}
        {state === "failed" && (<>
          <XCircle className="w-14 h-14 text-red-500 mx-auto mb-5" />
          <h1 className="font-display text-2xl font-black text-[#2D231F] mb-2">{t("pay.failed")}</h1>
          <Button onClick={() => nav("/dashboard")} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white mt-4">{t("pay.toDashboard")}</Button>
        </>)}
      </motion.div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useI18n();
  const nav = useNavigate();
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-5">
      <div className="max-w-md w-full text-center bg-white rounded-2xl border border-[#EBDDD3] p-10" data-testid="payment-cancel">
        <XCircle className="w-14 h-14 text-[#8C7A70] mx-auto mb-5" />
        <h1 className="font-display text-2xl font-black text-[#2D231F] mb-2">{t("pay.cancelled")}</h1>
        <Button onClick={() => nav("/dashboard")} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white mt-4">{t("pay.toDashboard")}</Button>
      </div>
    </div>
  );
}